/**
 * Weather File Library Types
 *
 * Types for the global weather file library, search, and caching.
 */

/**
 * Weather file source types from various meteorological databases.
 */
export type WeatherFileSourceType =
  | 'TMY3'
  | 'TMY2'
  | 'TMYx'
  | 'CWEC'
  | 'CWEC2016'
  | 'IWEC'
  | 'IWEC2'
  | 'SWERA'
  | 'ISHRAE'
  | 'AMY'
  | 'RMY'
  | 'ETMY'
  | 'IGDG'
  | 'INETI'
  | 'NIWA'
  | 'Other';

/**
 * ASHRAE climate zone identifiers.
 * Includes zones 0-8 with humidity suffixes (A=Humid, B=Dry, C=Marine).
 */
export type ASHRAEClimateZone =
  | '0A' | '0B'
  | '1A' | '1B'
  | '2A' | '2B'
  | '3A' | '3B' | '3C'
  | '4A' | '4B' | '4C'
  | '5A' | '5B' | '5C'
  | '6A' | '6B'
  | '7' | '7A' | '7B'
  | '8' | '8A' | '8B';

/**
 * Weather file location information.
 */
export interface WeatherFileLocation {
  /** City name */
  city: string;
  /** State/province/region */
  region: string;
  /** Country name */
  country: string;
  /** ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Latitude in decimal degrees */
  latitude: number;
  /** Longitude in decimal degrees */
  longitude: number;
  /** Elevation in meters above sea level */
  elevation: number;
  /** UTC timezone offset in hours */
  timezone: number;
  /** WMO station identifier (if available) */
  wmoId?: string;
}

/**
 * Climate zone classification information.
 */
export interface ClimateZoneInfo {
  /** ASHRAE 169 climate zone (e.g., "5B") */
  ashrae?: ASHRAEClimateZone | undefined;
  /** IECC climate zone */
  iecc?: string | undefined;
  /** Koppen-Geiger classification (e.g., "BSk") */
  koppen?: string | undefined;
  /** Climate zone description */
  description?: string | undefined;
}

/**
 * Weather file source/provenance information.
 */
export interface WeatherFileSource {
  /** Data source type */
  type: WeatherFileSourceType;
  /** Year range of data (e.g., "1991-2005") */
  years?: string;
  /** Data provider organization */
  provider?: string;
  /** Last update date (ISO format) */
  updated?: string;
}

/**
 * URLs for weather file resources.
 */
export interface WeatherFileUrls {
  /** Direct URL to EPW file */
  epw: string;
  /** URL to DDY (design day) file */
  ddy?: string;
  /** URL to STAT file */
  stat?: string;
  /** URL to additional metadata */
  metadata?: string;
}

/**
 * Climate statistics summary from EPW data.
 */
export interface ClimateStats {
  /** Average dry-bulb temperature (C) */
  avgTemp?: number;
  /** Minimum dry-bulb temperature (C) */
  minTemp?: number;
  /** Maximum dry-bulb temperature (C) */
  maxTemp?: number;
  /** Annual heating degree days (base 18C) */
  hdd18?: number;
  /** Annual cooling degree days (base 10C) */
  cdd10?: number;
  /** Annual heating degree days (base 65F) */
  hdd65?: number;
  /** Annual cooling degree days (base 50F) */
  cdd50?: number;
  /** Average direct normal irradiance (Wh/m2) */
  avgDNI?: number;
  /** Average global horizontal irradiance (Wh/m2) */
  avgGHI?: number;
  /** Average wind speed (m/s) */
  avgWindSpeed?: number;
  /** Prevailing wind direction (degrees) */
  prevailingWindDir?: number;
  /** Average relative humidity (%) */
  avgHumidity?: number;
  /** Annual precipitation (mm) */
  annualPrecip?: number;
}

/**
 * Complete weather file metadata record.
 */
export interface WeatherFileMetadata {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Original filename */
  filename: string;
  /** Location information */
  location: WeatherFileLocation;
  /** Climate zone classification */
  climateZone: ClimateZoneInfo;
  /** Data source information */
  source: WeatherFileSource;
  /** Resource URLs */
  urls: WeatherFileUrls;
  /** Climate statistics */
  stats?: ClimateStats;
  /** Popularity rank (0-100, higher is more popular) */
  popularity?: number;
  /** Descriptive tags */
  tags?: string[];
}

/**
 * Weather file search query parameters.
 */
export interface WeatherFileSearchQuery {
  /** Text search (city, country, region) */
  text?: string;
  /** Filter by country code */
  countryCode?: string;
  /** Filter by ASHRAE climate zone */
  climateZone?: ASHRAEClimateZone | ASHRAEClimateZone[];
  /** Filter by source type */
  sourceType?: WeatherFileSourceType | WeatherFileSourceType[];
  /** Filter by year range (e.g., "2004-2018", "2007-2021") */
  yearRange?: string | string[];
  /** Geographic center for radius search */
  center?: {
    latitude: number;
    longitude: number;
  };
  /** Search radius in kilometers */
  radiusKm?: number;
  /** Bounding box for map searches */
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  /** Minimum elevation (m) */
  minElevation?: number;
  /** Maximum elevation (m) */
  maxElevation?: number;
  /** Sort order */
  sortBy?: 'distance' | 'name' | 'popularity' | 'elevation';
  /** Sort direction */
  sortOrder?: 'asc' | 'desc';
  /** Maximum results to return */
  limit?: number;
  /** Offset for pagination */
  offset?: number;
}

/**
 * Weather file search result with additional computed fields.
 */
export interface WeatherFileSearchResult extends WeatherFileMetadata {
  /** Distance from search center (km), if geographic search */
  distance?: number;
  /** Search relevance score (0-1), if text search */
  score?: number;
}

/**
 * View modes for the weather file library.
 */
export type WeatherLibraryViewMode = 'list' | 'map' | 'climate-zones';

/**
 * Tab options for weather file preview.
 */
export type WeatherPreviewTab = 'info' | 'climate' | 'design-days' | 'raw';

/**
 * Cached weather file entry for IndexedDB.
 */
export interface CachedWeatherFile {
  /** Weather file ID */
  id: string;
  /** EPW file content */
  content: string;
  /** File size in bytes */
  size: number;
  /** When the file was cached */
  cachedAt: Date;
  /** When the file was last accessed */
  lastAccessed: Date;
  /** Access count */
  accessCount: number;
}

/**
 * Recently used weather file entry.
 */
export interface RecentWeatherFile {
  /** Weather file ID */
  id: string;
  /** Display name */
  name: string;
  /** Location for display */
  location: string;
  /** When it was last used */
  lastUsed: Date;
  /** Source type */
  sourceType: WeatherFileSourceType;
}

/**
 * Favorite weather file entry.
 */
export interface FavoriteWeatherFile {
  /** Weather file ID */
  id: string;
  /** When it was added to favorites */
  addedAt: Date;
  /** Optional user note */
  note?: string;
}

/**
 * Weather library user preferences.
 */
export interface WeatherLibraryPreferences {
  /** Default view mode */
  defaultView: WeatherLibraryViewMode;
  /** Show popularity indicators */
  showPopularity: boolean;
  /** Preferred units (metric/imperial) */
  units: 'metric' | 'imperial';
  /** Maximum cache size in MB */
  maxCacheSize: number;
  /** Maximum cached files */
  maxCachedFiles: number;
  /** Recently used files to keep */
  recentFilesLimit: number;
}

/**
 * Default weather library preferences.
 */
export const DEFAULT_WEATHER_LIBRARY_PREFERENCES: WeatherLibraryPreferences = {
  defaultView: 'list',
  showPopularity: true,
  units: 'metric',
  maxCacheSize: 100, // MB
  maxCachedFiles: 50,
  recentFilesLimit: 20,
};

// ============ Design Day Types ============

/**
 * Design day type classification.
 */
export type DesignDayType = 'heating' | 'cooling' | 'extreme' | 'dehumidification' | 'humidification' | 'other';

/**
 * Design day category.
 */
export type DesignDayCategory = 'annual' | 'monthly' | 'extreme';

/**
 * Design day metadata for the index (without full IDF object).
 * This is the pre-indexed data loaded with the weather file.
 */
export interface DesignDayMetadata {
  /** Unique ID within this weather file (index-based) */
  id: string;
  /** Design day name from DDY file */
  name: string;
  /** Type classification */
  type: DesignDayType;
  /** Category (annual, monthly, extreme) */
  category: DesignDayCategory;
  /** Month (1-12) */
  month: number;
  /** Day of month */
  dayOfMonth: number;
  /** Day type (WinterDesignDay, SummerDesignDay, etc.) */
  dayType: string;
  /** Maximum dry-bulb temperature (°C) */
  dryBulbTemp: number;
  /** Wet-bulb temperature (°C) */
  wetBulbTemp?: number;
  /** Humidity ratio or RH */
  humidity?: number;
  /** Wind speed (m/s) */
  windSpeed?: number;
  /** Wind direction (degrees) */
  windDirection?: number;
}

/**
 * Full parsed design day with IDF object (fetched on demand).
 */
export interface ParsedDesignDay extends DesignDayMetadata {
  /** Full IDF object text for injection */
  idfObject: string;
  /** Parsed field values */
  fields: Array<{ name: string; value: string; units?: string }>;
}

/**
 * DDY index data for a single weather file (pre-indexed metadata).
 */
export interface DdyIndexEntry {
  /** Total design days in DDY file */
  count: number;
  /** Design day metadata array */
  designDays: DesignDayMetadata[];
}

/**
 * DDY index for a country (metadata only).
 */
export interface DdyCountryIndex {
  [fileId: string]: DdyIndexEntry;
}

/**
 * Design summary data for a weather file (Tier 3).
 */
export interface DesignConditionsSummary {
  heating: {
    /** 99.6% dry bulb temperature */
    db99_6?: number;
    /** 99% dry bulb temperature */
    db99?: number;
    /** Wind speed at 99.6% */
    ws99_6?: number;
  };
  cooling: {
    /** 1% dry bulb temperature */
    db1?: number;
    /** 2% dry bulb temperature */
    db2?: number;
    /** 1% wet bulb temperature */
    wb1?: number;
    /** Mean coincident dry bulb */
    mcdb?: number;
  };
  extreme: {
    /** Extreme minimum dry bulb */
    minDb?: number;
    /** Extreme maximum dry bulb */
    maxDb?: number;
    /** Extreme maximum wet bulb */
    maxWb?: number;
  };
}

/**
 * Design data index for a country.
 */
export interface DesignCountryIndex {
  [fileId: string]: DesignConditionsSummary;
}

/**
 * Monthly climate data for a weather file (Tier 2).
 */
export interface MonthlyClimateEntry {
  month: number;
  avgTemp: number;
  minTemp: number;
  maxTemp: number;
  avgHumidity: number;
  avgWindSpeed: number;
  avgGHI: number;
  avgDNI: number;
  totalPrecip: number;
  hdd: number;
  cdd: number;
}

/**
 * Climate data index for a country.
 * Maps file IDs directly to their monthly climate data arrays.
 */
export interface ClimateCountryIndex {
  [fileId: string]: MonthlyClimateEntry[];
}

/**
 * Result of injecting design days into IDF.
 */
export interface DesignDayInjectionResult {
  added: number;
  skipped: number;
  replaced: number;
  errors: string[];
}

/**
 * Options for design day injection.
 */
export interface DesignDayInjectionOptions {
  /** How to handle conflicts with existing design days */
  onConflict: 'replace' | 'rename' | 'skip';
}

/**
 * ASHRAE climate zone definitions with descriptions.
 */
export const ASHRAE_CLIMATE_ZONES: Record<string, { name: string; description: string }> = {
  '0A': { name: 'Extremely Hot - Humid', description: 'Extremely hot and humid (equatorial regions)' },
  '0B': { name: 'Extremely Hot - Dry', description: 'Extremely hot and dry (desert regions)' },
  '1A': { name: 'Very Hot - Humid', description: 'Very hot and humid (Miami, Singapore)' },
  '1B': { name: 'Very Hot - Dry', description: 'Very hot and dry (extremely rare)' },
  '2A': { name: 'Hot - Humid', description: 'Hot and humid (Houston, New Orleans)' },
  '2B': { name: 'Hot - Dry', description: 'Hot and dry (Phoenix, Las Vegas)' },
  '3A': { name: 'Warm - Humid', description: 'Warm and humid (Atlanta, Dallas)' },
  '3B': { name: 'Warm - Dry', description: 'Warm and dry (Los Angeles, El Paso)' },
  '3C': { name: 'Warm - Marine', description: 'Warm and marine (San Francisco)' },
  '4A': { name: 'Mixed - Humid', description: 'Mixed and humid (New York, Washington DC)' },
  '4B': { name: 'Mixed - Dry', description: 'Mixed and dry (Albuquerque)' },
  '4C': { name: 'Mixed - Marine', description: 'Mixed and marine (Seattle, Portland)' },
  '5A': { name: 'Cool - Humid', description: 'Cool and humid (Chicago, Boston)' },
  '5B': { name: 'Cool - Dry', description: 'Cool and dry (Denver, Salt Lake City)' },
  '5C': { name: 'Cool - Marine', description: 'Cool and marine (Vancouver)' },
  '6A': { name: 'Cold - Humid', description: 'Cold and humid (Minneapolis, Montreal)' },
  '6B': { name: 'Cold - Dry', description: 'Cold and dry (Helena, Billings)' },
  '7': { name: 'Very Cold', description: 'Very cold (Duluth, Fairbanks)' },
  '7A': { name: 'Very Cold - Humid', description: 'Very cold and humid' },
  '7B': { name: 'Very Cold - Dry', description: 'Very cold and dry' },
  '8': { name: 'Subarctic', description: 'Subarctic (extreme northern regions)' },
  '8A': { name: 'Subarctic - Humid', description: 'Subarctic and humid' },
  '8B': { name: 'Subarctic - Dry', description: 'Subarctic and dry' },
};

/**
 * Country code to name mapping for common countries.
 */
export const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  MX: 'Mexico',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  ES: 'Spain',
  IT: 'Italy',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  AU: 'Australia',
  NZ: 'New Zealand',
  BR: 'Brazil',
  AR: 'Argentina',
  CL: 'Chile',
  ZA: 'South Africa',
  EG: 'Egypt',
  AE: 'United Arab Emirates',
  SA: 'Saudi Arabia',
  KR: 'South Korea',
  SG: 'Singapore',
  TH: 'Thailand',
  MY: 'Malaysia',
  ID: 'Indonesia',
  PH: 'Philippines',
  VN: 'Vietnam',
  RU: 'Russia',
  PL: 'Poland',
  NL: 'Netherlands',
  BE: 'Belgium',
  AT: 'Austria',
  CH: 'Switzerland',
  SE: 'Sweden',
  NO: 'Norway',
  FI: 'Finland',
  DK: 'Denmark',
  IE: 'Ireland',
  PT: 'Portugal',
  GR: 'Greece',
  TR: 'Turkey',
  IL: 'Israel',
  HK: 'Hong Kong',
  TW: 'Taiwan',
};
